import apiSlice from '..';
import { convertToFormData } from '../../../utils/api';
import { User } from '../../../types/user';
import { closeLinkAccountDialog, snackShow, updateLinkedUser } from '../../../actions';
import { setToken, setUser } from '../../../utils/auth';

type AskQuestionPayload = {
  vehicle: string;
  vehicleId: number;
  email: string;
  fullName: string;
  phone: string;
  message: string;
};

type LinkedUser = {
  linked_user_id: number;
  linked_user_role: string;
  linked_user_name: string;
};

export const authApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    login: builder.mutation<any, any>({
      query: body => ({
        url: '/auth/login',
        method: 'POST',
        body
      }),
      transformErrorResponse: (r: any) => r.data,
      transformResponse: (r: any) => r.data
    }),
    forgotPassword: builder.mutation<any, any>({
      query: body => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body
      }),
      transformErrorResponse: (r: any) => r.data,
      transformResponse: (r: any) => r.data
    }),
    register: builder.mutation<any, any>({
      query: body => {
        const formData = convertToFormData(body);
        return {
          url: '/auth/register',
          method: 'POST',
          body: formData,
          formData: true
        };
      },
      transformErrorResponse: (r: any) => r.data,
      transformResponse: (r: any) => r.data
    }),
    onboarding: builder.mutation<any, any>({
      query: body => {
        const formData = convertToFormData(body);
        return {
          url: '/auth/onboarding',
          method: 'POST',
          body: formData,
          formData: true
        };
      },
      transformErrorResponse: (r: any) => r.data,
      transformResponse: (r: any) => r.data
    }),
    me: builder.query<User, void>({
      query: () => ({
        url: '/me'
      }),
      providesTags: ['Me'],
      transformResponse: (r: any) => r.data
    }),
    validateForm: builder.mutation<any, any>({
      query: body => ({
        method: 'POST',
        url: 'auth/validate',
        body
      }),
      transformErrorResponse: (r: any) => r.data,
      transformResponse: (r: any) => r.data
    }),
    askQuestion: builder.mutation<void, AskQuestionPayload>({
      query: body => ({
        url: '/auth/contact-question',
        method: 'POST',
        body
      })
    }),
    linkAccount: builder.mutation<{ data: LinkedUser }, { email: string; password: string }>({
      query: body => ({
        url: '/me/link-account',
        method: 'POST',
        body
      }),
      async onQueryStarted(arg, api) {
        try {
          const result = await api.queryFulfilled;
          api.dispatch(closeLinkAccountDialog());
          api.dispatch(updateLinkedUser(result.data.data));
          api.dispatch(snackShow({ message: 'Account linked successfully', type: 'success' }));
        } catch (error: any) {
          console.log(error, 'error');
          api.dispatch(closeLinkAccountDialog());
          api.dispatch(snackShow({ message: error?.error?.data?.message || 'Failed to link account', type: 'error' }));
        }
      }
    }),
    switchUser: builder.mutation<{ data: { token: string; user: User } }, void>({
      query: () => ({
        url: '/me/switch-account',
        method: 'POST'
      }),
      async onQueryStarted(arg, api) {
        try {
          const result = await api.queryFulfilled;
          setUser(result.data.data.user);
          setToken(result.data.data.token);
          if (result.data.data.user.role === 'buyer') {
            window.location.href = '/';
          } else {
            window.location.href = '/seller/dashboard';
          }
        } catch (error: any) {
          api.dispatch(
            snackShow({ message: error?.error?.data?.message || 'Failed to switch account', type: 'error' })
          );
        }
      }
    })
  }),
  overrideExisting: true
});

export const {
  useLoginMutation,
  useForgotPasswordMutation,
  useRegisterMutation,
  useOnboardingMutation,
  useMeQuery,
  useValidateFormMutation,
  useAskQuestionMutation,
  useLinkAccountMutation,
  useSwitchUserMutation
} = authApiSlice;
